/**
 * Virdio Health — Browser & Device Detection Utility
 * 
 * Detects the user's device (Mac, Windows, iPhone, Android) and browser,
 * then checks against Virdio's supported combinations.
 * 
 * Supported:
 *   Mac     → Chrome, Edge, Safari
 *   Windows → Chrome, Edge
 *   iPhone  → Safari, Chrome
 *   Android → Chrome
 * 
 * Usage:
 *   import { checkBrowserSupport } from './browserDetect';
 *   const result = checkBrowserSupport('gsa');  // or 'member'
 *   if (!result.isSupported) { show block screen with result }
 */

// ── Detection Logic ──────────────────────────────────────────────

function detectDevice() {
  const ua = navigator.userAgent;

  // iPhone / iPod (check before Mac since iPads may spoof Mac UA)
  if (/iPhone|iPod/.test(ua)) return 'iPhone';

  // iPad — modern iPads report as Mac, so also check touch support
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'iPhone';

  // Android
  if (/Android/.test(ua)) return 'Android';

  // Windows
  if (/Windows/.test(ua)) return 'Windows';

  // Mac (desktop — after iPad check)
  if (/Macintosh|Mac OS X/.test(ua)) return 'Mac';

  return 'Unknown';
}

function detectBrowser() {
  const ua = navigator.userAgent;

  // Edge (Chromium-based Edge includes "Edg/")
  if (/Edg\//.test(ua)) return 'Edge';

  // Chrome (must check after Edge since Edge also contains "Chrome")
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'Chrome';

  // Safari (must check after Chrome since Chrome also contains "Safari")
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari';

  // Firefox
  if (/Firefox\//.test(ua)) return 'Firefox';

  // Samsung Internet
  if (/SamsungBrowser/.test(ua)) return 'Samsung Internet';

  // Opera
  if (/OPR\//.test(ua) || /Opera/.test(ua)) return 'Opera';

  return 'Unknown';
}

// ── Supported Combinations ───────────────────────────────────────

const SUPPORTED = {
  Mac: ['Safari', 'Chrome', 'Edge'],
  Windows: ['Chrome', 'Edge'],
  iPhone: ['Safari', 'Chrome'],
  Android: ['Chrome'],
};

// ── Message Generation ───────────────────────────────────────────

const FEATURE_LABELS = {
  gsa: 'Guided Self-Assessment',
  member: 'Guided Exercise Sessions',
};

const DEVICE_LABELS = {
  Mac: 'Mac',
  Windows: 'Windows PC',
  iPhone: 'iPhone',
  Android: 'Android device',
};

function formatBrowserList(browsers) {
  if (browsers.length === 1) return browsers[0];
  if (browsers.length === 2) return `${browsers[0]} or ${browsers[1]}`;
  return `${browsers.slice(0, -1).join(', ')}, or ${browsers[browsers.length - 1]}`;
}

function buildMessage(device, featureType) {
  const feature = FEATURE_LABELS[featureType] || FEATURE_LABELS.gsa;
  const usesVerb = featureType === 'member' ? 'use' : 'uses';

  const footerText = `Virdio Health's ${feature} ${usesVerb} your camera, voice, and motion tracking to guide your recovery, and these browsers ensure everything works seamlessly.`;

  // Known device, unsupported browser
  if (SUPPORTED[device]) {
    const browsers = SUPPORTED[device];
    const deviceLabel = DEVICE_LABELS[device];
    const browserList = formatBrowserList(browsers);

    const isSingleBrowser = browsers.length === 1;

    const heading = `On your ${deviceLabel}, ${feature} works best on ${isSingleBrowser ? '' : 'these browsers: '}${browserList}.`;
    const action = isSingleBrowser
      ? `To continue, please switch to ${browsers[0]}.`
      : 'To continue, please switch to one of the supported browsers above.';

    return { heading, action, footer: footerText, showFullList: false };
  }

  // Unknown device — show everything
  const heading = `${feature} is supported on the following browsers and devices:`;
  const fullList = [
    { device: 'Mac', browsers: 'Safari, Chrome, or Edge' },
    { device: 'Windows', browsers: 'Chrome or Edge' },
    { device: 'iPhone', browsers: 'Safari or Chrome' },
    { device: 'Android', browsers: 'Chrome' },
  ];
  const action = 'To continue, please switch to a supported browser and device above.';

  return { heading, action, footer: footerText, showFullList: true, fullList };
}

// ── Main Export ──────────────────────────────────────────────────

/**
 * checkBrowserSupport('gsa' | 'member')
 * 
 * Returns:
 *   {
 *     isSupported: boolean,
 *     device: string,
 *     browser: string,
 *     message: { heading, action, footer, showFullList, fullList? }
 *   }
 */
export function checkBrowserSupport(featureType = 'gsa') {
  const device = detectDevice();
  const browser = detectBrowser();

  const supportedBrowsers = SUPPORTED[device];
  const isSupported = supportedBrowsers
    ? supportedBrowsers.includes(browser)
    : false;

  const message = isSupported ? null : buildMessage(device, featureType);

  return { isSupported, device, browser, message };
}

export default checkBrowserSupport;